import React, { useEffect, useState } from 'react';

import classNames from 'classnames';
import AvailableAttachments from 'containers/AvailableAttachments';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import CheckboxField from 'components/application/CheckboxField';
import Toggleable from 'components/application/Toggleable';
import ToggleableRow from 'components/application/ToggleableRow';

import SubmittableStepField from 'components/sequences/SubmittableStepField';

export default function StepRow({
  addError,
  dispatch,
  isEditingToggled,
  step,
  stepFieldText,
  stepNameKey,
  stepsListIsOrdering,
}) {
  const [isCheckboxDisabled, setIsCheckboxDisabled] = useState(false);

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const handleStepNameChange = ({ content }) => {
    dispatch({
      key: `stepName--${step.id}`,
      type: 'EDITING_CONTENT_CHANGE',
      value: content,
    });
  };

  const handleChange = (event) => {
    setIsCheckboxDisabled(true);

    axios
      .patch(`/method_steps/${step.id}`, {
        method_step: {
          selected: event.target.checked,
        },
      })
      .then((response) => {
        dispatch({ type: 'UPDATE_RESOURCE', payload: response.data });
      })
      .catch(handleError);
  };

  const requestStepUpdate = () => {
    axios
      .patch(`/method_steps/${step.id}`, {
        method_step: {
          text: stepFieldText,
        },
      })
      .then((response) => {
        dispatch({ type: 'UPDATE_RESOURCE', payload: response.data });
        dispatch({ type: 'CLEAR_TOGGLE_CONTENT', key: stepNameKey });
      })
      .catch(handleError);
  };

  const handleError = () =>
    addError(uuidv4(), { fullMessage: i18nTShared('errors.generic') });

  useEffect(() => {
    setIsCheckboxDisabled(false);
  }, [step.attributes.selected]);

  return (
    <ToggleableRow
      checkboxField={
        <CheckboxField
          checked={!!step.attributes.selected}
          disabled={isCheckboxDisabled}
          height='short'
          labelTextProps={{ className: 'tw-font-medium' }}
          name='stepSelected'
          onChange={handleChange}
          value={step.id}
        />
      }
      isOrdering={stepsListIsOrdering}
      modifiers={['secondary', 'border-top-none']}
      toggleableField={
        <>
          <div className='tw-flex'>
            <div className='tw-min-w-0 tw-flex-1'>
              <Toggleable
                isToggled={isEditingToggled}
                primary={
                  <div className='toggleable-field__item-wrapper'>
                    <div
                      className={classNames(
                        'rich-text tw-border-grey-500 [&_p]:tw-m-0',
                        stepsListIsOrdering &&
                          'truncated-text-container tw-h-5',
                      )}
                    >
                      {parse(DOMPurify.sanitize(step.attributes.text))}
                    </div>
                  </div>
                }
                secondary={
                  <SubmittableStepField
                    onCancel={() =>
                      dispatch({
                        type: 'CLEAR_TOGGLE_CONTENT',
                        key: stepNameKey,
                      })
                    }
                    onChange={handleStepNameChange}
                    onSubmit={requestStepUpdate}
                    submitButtonText='Update'
                    value={stepFieldText}
                  />
                }
                setIsToggled={(toggleValue) =>
                  dispatch({
                    key: stepNameKey,
                    type: 'UI_TOGGLE',
                    value: toggleValue,
                  })
                }
              />
            </div>
            <div className='tw-ml-4'>
              <span id={`add-attachment-button-${step.id}`} />
            </div>
          </div>
          {!stepsListIsOrdering && (
            <div>
              <AvailableAttachments
                accordionContentContainerClassName='tw-mt-4 tw--ml-8'
                addImagesButtonPortalId={`add-attachment-button-${step.id}`}
                attachableId={step.id}
                attachablePlural='method_steps'
                attachableType='MethodStep'
              />
            </div>
          )}
        </>
      }
    />
  );
}
